/* PUMPLAB_PDP_ADDONS_V1: add-on cards, direct add/remove, shared mini-PDP dialog. */
import { animate, inView, stagger } from "vendor";
// Versioned core URL from Liquid (asset_url ?v=): a bare "./pl-addons-core.js" import can pair a new
// pl-addons.js with a stale cached core after a deploy, and the module then fails to link.
const coreUrl = document.querySelector("pl-addons[data-core-url]")?.dataset.coreUrl
  || new URL("./pl-addons-core.js", import.meta.url).href;
const { findAddonLine, queueCartOp, describeError, clampIndex } = await import(coreUrl);

const SNAP = [0.22, 1, 0.36, 1];
const EXIT = [0.55, 0, 1, 0.45];
const root = () => (window.Shopify && Shopify.routes && Shopify.routes.root) || "/";
const isMobile = () => window.matchMedia("(max-width: 999px)").matches;

// Current site ATC look (PUMPLAB_ATC_DOUBLE_RIPPLE_V2, same as buy-buttons.liquid): tag the button and
// append the white radial span only after CustomButton has wrapped its content (it appends .button__loader).
function tagRipple(btn, tries = 0) {
  if (!btn || btn.classList.contains("pl-ripple")) return;
  if (!btn.querySelector(".button__loader")) {
    if (tries < 90) requestAnimationFrame(() => tagRipple(btn, tries + 1));
    return;
  }
  btn.classList.add("pl-ripple");
  const rip = document.createElement("span");
  rip.className = "pl-atc-rip";
  rip.setAttribute("aria-hidden", "true");
  btn.appendChild(rip);
}

function bundledSections() {
  const sections = [];
  document.documentElement.dispatchEvent(
    new CustomEvent("cart:prepare-bundled-sections", { bubbles: true, detail: { sections } })
  );
  return sections;
}

async function postJson(path, body) {
  const response = await fetch(`${root()}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json", "X-Requested-With": "XMLHttpRequest" },
    body: JSON.stringify(body)
  });
  const json = await response.json().catch(() => null);
  if (!response.ok) throw json || {};
  return json;
}

class PlAddons extends HTMLElement {
  connectedCallback() {
    this._abort = new AbortController();
    const signal = this._abort.signal;
    this._cart = null;
    this._active = null;
    this._dialog = this.querySelector("[data-addon-dialog]");
    if (this._dialog) document.body.appendChild(this._dialog); // escape sticky/overflow of the buy box
    this._intro();
    this.querySelectorAll(".pl-addon product-card").forEach((pc) => this._placeholders(pc));

    this.cards = Array.from(this.querySelectorAll("[data-addon-card]")).map((el) => {
      const data = JSON.parse(el.querySelector("[data-addon-data]").textContent);
      const card = { el, data, variantId: data.selected };
      tagRipple(el.querySelector("[data-addon-add]"));
      // Multi-flavour add-ons: the button reads "Välj smak" and opens the mini-PDP (like the product-card quick-add)
      const addButton = el.querySelector("[data-addon-add]");
      card.choose = addButton.hasAttribute("data-addon-choose");
      addButton.addEventListener("click", () => {
        if (card.choose) this._open(card, addButton);
        else this._add(card);
      }, { signal });
      el.querySelectorAll("[data-addon-more]").forEach((trigger) => {
        trigger.addEventListener("click", () => this._open(card, trigger), { signal });
      });
      // The whole product card opens the mini-PDP (Evora: image + "Les mer" both carry data-xs-open).
      // Modifier / middle clicks keep the normal link, so the product page still opens in a new tab.
      const productCard = el.querySelector("product-card");
      if (productCard) {
        productCard.addEventListener("click", (e) => {
          if (e.target.closest(".pl-addon__foot")) return;
          if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
          e.preventDefault();
          e.stopPropagation();
          this._open(card, productCard.querySelector("a[href]") || undefined);
        }, { signal, capture: true });
      }
      return card;
    });

    // Card row arrows (desktop): one card per click, disabled at the ends
    const grid = this.querySelector(".pl-addons__grid");
    const rail = this.querySelector(".pl-addons__rail");
    const prev = this.querySelector("[data-addons-prev]");
    const next = this.querySelector("[data-addons-next]");
    // Edge fades follow the bestseller carousel (PUMPLAB_FEATURED_FADE_OVERLAY_V3): right fade hides at the end,
    // left fade only shows once scrolled
    const syncFade = () => {
      if (!grid || !rail) return;
      rail.classList.toggle("is-at-start", grid.scrollLeft <= 2);
      rail.classList.toggle("is-at-end", grid.scrollLeft + grid.clientWidth >= grid.scrollWidth - 2);
    };
    if (rail) {
      // Fade into the real page background (the block's own --background is the card colour, not the page)
      let node = rail.parentElement, bg = "";
      while (node && !bg) {
        const c = getComputedStyle(node).backgroundColor;
        if (c && c !== "rgba(0, 0, 0, 0)" && c !== "transparent") bg = c;
        node = node.parentElement;
      }
      if (bg) rail.style.setProperty("--pl-edge-bg", bg);
    }
    if (grid) { grid.addEventListener("scroll", syncFade, { passive: true, signal }); syncFade(); }
    if (grid && prev && next) {
      const step = () => (grid.querySelector(".pl-addon")?.getBoundingClientRect().width || grid.clientWidth / 2.5) + 10;
      const sync = () => {
        prev.disabled = grid.scrollLeft <= 2;
        next.disabled = grid.scrollLeft + grid.clientWidth >= grid.scrollWidth - 2;
      };
      prev.addEventListener("click", () => grid.scrollBy({ left: -step(), behavior: "smooth" }), { signal });
      next.addEventListener("click", () => grid.scrollBy({ left: step(), behavior: "smooth" }), { signal });
      grid.addEventListener("scroll", sync, { passive: true, signal });
      sync();
    }

    document.addEventListener("cart:change", (e) => {
      if (e.detail && e.detail.cart) this._paintAll(e.detail.cart);
    }, { signal });
    // Theme fires cart:refresh (and the page can come back from bfcache) without a cart payload
    document.addEventListener("cart:refresh", () => this._refetchCart(), { signal });
    window.addEventListener("pageshow", (e) => { if (e.persisted) this._refetchCart(); }, { signal });

    if (this._dialog) {
      this._dialog.querySelector("[data-dlg-close]").addEventListener("click", () => this._close(), { signal });
      this._dialog.addEventListener("click", (e) => { if (e.target === this._dialog) this._close(); }, { signal });
      this._dialog.addEventListener("cancel", (e) => { e.preventDefault(); this._close(); }, { signal });
      // A close we did not drive (browser close-watcher, Android back) must still unlock the page
      this._dialog.addEventListener("close", () => { if (!this._closing) this._afterClose(); }, { signal });
      this._dialog.addEventListener("keydown", (e) => {
        if (e.target.closest && e.target.closest("select, input")) return; // radios own their arrow keys
        if (e.key === "ArrowLeft") this._go(-1);
        if (e.key === "ArrowRight") this._go(1);
      }, { signal });
    }

    this._refetchCart();
  }

  _refetchCart() {
    return fetch(`${root()}cart.js`, { headers: { Accept: "application/json" } })
      .then((r) => r.json())
      .then((cart) => this._paintAll(cart))
      .catch(() => {});
  }

  // Image placeholder: the image's box pulses (theme "pulse" skeleton) until it has loaded, then the image fades in.
  _placeholders(root) {
    root.querySelectorAll("img").forEach((img) => {
      const box = img.parentElement;
      if (!box || box.classList.contains("pl-img-ph")) return;
      if (img.complete && img.naturalWidth) return;
      box.classList.add("pl-img-ph");
      img.classList.add("pl-img-ph__img");
      const done = () => { box.classList.remove("pl-img-ph"); img.classList.add("is-loaded"); };
      // failed: keep a calm tile in the same box (no pulse, no broken-image icon) so the layout holds
      const failed = () => { box.classList.add("pl-img-ph--failed"); img.hidden = true; };
      img.addEventListener("load", done, { once: true });
      img.addEventListener("error", failed, { once: true });
    });
  }

  // Entrance when the section scrolls into view: heading fades up, cards follow in a short stagger (once per view).
  _intro() {
    if (matchMedia("(prefers-reduced-motion: reduce)").matches || window.Shopify?.designMode) return;
    const head = this.querySelector(".pl-addons__head");
    const cards = Array.from(this.querySelectorAll(".pl-addon"));
    if (!cards.length) return;
    this.classList.add("is-pre");
    const reveal = () => {
      if (!this.classList.contains("is-pre")) return;
      this.classList.remove("is-pre");
      if (head) animate(head, { opacity: [0, 1], transform: ["translateY(10px)", "translateY(0)"] }, { duration: 0.4, delay: 0.15, easing: SNAP });
      const run = animate(cards, { opacity: [0, 1], transform: ["translateY(18px)", "translateY(0)"] }, { duration: 0.5, delay: stagger(0.07, { start: 0.25 }), easing: SNAP });
      // clear inline transforms afterwards so the cards never keep a stacking context (tab + hover lift)
      Promise.race([run.finished, new Promise((r) => setTimeout(r, 1400))]).then(() => {
        [head, ...cards].forEach((el) => { if (el) { el.style.transform = ""; el.style.opacity = ""; } });
      });
    };
    const stop = inView(this, () => { reveal(); stop(); }, { amount: 0.2 });
    this._abort.signal.addEventListener("abort", () => { stop(); reveal(); });
  }

  disconnectedCallback() {
    this._abort.abort();
    if (this._dialog && this._dialog.open) this._dialog.close();
    if (this._dialog) this._dialog.remove();
    document.documentElement.classList.remove("lock");
  }

  _variantIds(card) { return card.data.variants.map((v) => v.id); }
  _variant(card) { return card.data.variants.find((v) => v.id === card.variantId) || card.data.variants[0]; }
  // State follows the SELECTED flavour, so another flavour already in the cart is never the one removed
  _line(card) { return this._cart ? findAddonLine(this._cart, [card.variantId]) : null; }

  _paintAll(cart) {
    this._cart = cart;
    this.cards.forEach((card) => {
      // Show the flavour that is actually in the cart, so "I varukorgen" names the right one
      // On a fresh paint, preselect a flavour that is already in the cart (any flavour of this add-on)
      if (!this._line(card)) {
        const anyLine = findAddonLine(cart, this._variantIds(card));
        if (anyLine && !card.touched) card.variantId = anyLine.variantId;
      }
      this._paint(card);
    });
    if (this._active) this._paintDialog(this._active);
  }

  _paint(card) {
    // Short confirmation right after an add from THIS card ("✓ Tillagd", ~2 s), then back to normal.
    // Deliberately not tied to cart contents: older cart lines or another flavour must not relabel the card.
    const button = card.el.querySelector("[data-addon-add]");
    button.classList.toggle("is-added", !!card.justAdded);
    card.el.querySelector("[data-addon-add-label]").textContent = card.justAdded ? card.data.added : (card.choose ? card.data.choose : card.data.add);
  }

  _setVariant(card, id) {
    const changed = card.variantId !== id;
    card.variantId = id;
    card.touched = true;
    this._paint(card);
    if (this._active === card) {
      this._paintDialog(card);
      if (changed) this._renderGallery(card);
    }
  }

  // Add one unit, then confirm the way the product page does: CustomButton loader, then the cart drawer opens
  // (variant:add + cart:change, same events as product-form in theme.js).
  _add(card) {
    if (card.el.classList.contains("is-busy")) return Promise.resolve();
    const errorEl = card.el.querySelector("[data-addon-error]");
    errorEl.hidden = true;
    card.el.classList.add("is-busy");
    const addButton = card.el.querySelector("[data-addon-add]");
    addButton.setAttribute("aria-busy", "true");
    const fromDialog = this._active === card;
    const cta = fromDialog ? this._dialog.querySelector("[data-dlg-cta]") : null;
    if (cta) { cta.disabled = true; cta.setAttribute("aria-busy", "true"); }

    return queueCartOp(window, async () => {
      const sections = ["variant-added", ...bundledSections()];
      const added = await postJson("cart/add.js", { items: [{ id: card.variantId, quantity: 1 }], sections });
      const cart = await (await fetch(`${root()}cart.js`, { headers: { Accept: "application/json" } })).json();
      cart.sections = added.sections;
      if (fromDialog) await this._close();
      this.dispatchEvent(new CustomEvent("variant:add", { bubbles: true, detail: { items: added.items || [added], cart } }));
      document.documentElement.dispatchEvent(new CustomEvent("cart:change", { bubbles: true, detail: { baseEvent: "variant:add", cart } }));
      card.justAdded = true;
      this._paint(card);
      clearTimeout(card.addedTimer);
      card.addedTimer = setTimeout(() => { card.justAdded = false; this._paint(card); }, 2200);
    })
      .catch((err) => {
        errorEl.textContent = describeError(err, this.dataset.error);
        errorEl.hidden = false;
        if (this._active === card) this._paintDialog(card, errorEl.textContent);
      })
      .finally(() => {
        card.el.classList.remove("is-busy");
        addButton.removeAttribute("aria-busy");
        if (cta) { cta.disabled = false; cta.removeAttribute("aria-busy"); }
      });
  }

  _open(card, trigger) {
    if (!this._dialog || this._dialog.open) return;
    this._active = card;
    this._trigger = trigger || card.el.querySelector("[data-addon-more]");
    const body = this._dialog.querySelector("[data-dlg-body]");
    body.replaceChildren(card.el.querySelector("template[data-addon-modal]").content.cloneNode(true));
    // The star gradients share ids with the card's own rating, which is display:none on mobile;
    // url(#id) would resolve to that hidden copy and render hollow stars, so give the modal its own ids.
    body.querySelectorAll("svg [id]").forEach((def) => {
      const id = def.id;
      def.id = `${id}-dlg`;
      body.querySelectorAll(`[fill="url(#${id})"]`).forEach((el) => el.setAttribute("fill", `url(#${id}-dlg)`));
    });

    const track = body.querySelector("[data-dlg-track]");
    if (track) track.addEventListener("scroll", () => this._syncDots(), { passive: true });
    this._bindDesc(body);
    const prev = body.querySelector("[data-dlg-prev]");
    const next = body.querySelector("[data-dlg-next]");
    if (prev) prev.addEventListener("click", () => this._go(-1));
    if (next) next.addEventListener("click", () => this._go(1));
    body.querySelectorAll("[data-dlg-variant-input]").forEach((input) => {
      input.addEventListener("change", () => { if (input.checked) this._setVariant(card, Number(input.value)); });
    });
    body.querySelector("[data-dlg-cta]").addEventListener("click", () => this._add(card));

    // Cap the flavour dropdown to the space above the CTA so the open list never runs over it
    const ddTrigger = body.querySelector(".pl-variant-dd__trigger");
    if (ddTrigger) {
      ddTrigger.addEventListener("click", () => {
        const ctaTop = this._dialog.querySelector("[data-dlg-cta]").getBoundingClientRect().top;
        const room = Math.max(140, Math.floor(ctaTop - ddTrigger.getBoundingClientRect().bottom - 12));
        ddTrigger.closest(".pl-variant-dd").style.setProperty("--pl-dd-max", `${Math.min(room, 320)}px`);
      }, true);
    }

    const reviews = body.querySelector("[data-reviews-href]");
    if (reviews) {
      const go = () => { window.location.href = reviews.dataset.reviewsHref; };
      reviews.addEventListener("click", go);
      reviews.addEventListener("keydown", (e) => { if (e.key === "Enter") go(); });
    }

    body.querySelectorAll(".pl-addons-dlg__slide img[loading='lazy']").forEach((img) => { img.loading = "eager"; });
    this._placeholders(body.querySelector("[data-dlg-track]") || body);
    this._paintDialog(card);
    this._renderGallery(card);
    tagRipple(body.querySelector("[data-dlg-cta]"));
    this._dialog.showModal();
    document.documentElement.classList.add("lock");
    const panel = this._dialog.querySelector(".pl-addons-dlg__panel");
    animate(panel, isMobile()
      ? { transform: ["translateY(100%)", "translateY(0)"] }
      : { opacity: [0, 1], transform: ["translateY(24px) scale(.98)", "translateY(0) scale(1)"] },
      { duration: 0.42, easing: SNAP });
    // A stalled entrance (skipped frame, throttled tab) would leave the panel invisible or half-drawn;
    // finish it once it should be done so the modal never sticks black or flickers.
    setTimeout(() => { panel.getAnimations().forEach((a) => { try { a.finish(); } catch (e) { /* already done */ } }); }, 600);
  }

  async _close() {
    if (!this._dialog || !this._dialog.open || this._closing) return;
    this._closing = true;
    const panel = this._dialog.querySelector(".pl-addons-dlg__panel");
    try {
      const exit = animate(panel, isMobile()
        ? { transform: ["translateY(0)", "translateY(100%)"] }
        : { opacity: [1, 0], transform: ["translateY(0) scale(1)", "translateY(16px) scale(.98)"] },
        { duration: 0.24, easing: EXIT });
      // A paused animation (hidden tab, interrupted frame) never settles `finished`; never let that block the close.
      await Promise.race([exit.finished, new Promise((r) => setTimeout(r, 400))]);
    } catch (e) { /* animation interrupted: close anyway */ }
    this._dialog.close();
    panel.style.transform = "";
    panel.style.opacity = "";
    this._afterClose();
    this._closing = false;
  }

  _afterClose() {
    document.documentElement.classList.remove("lock");
    const trigger = this._trigger;
    this._active = null;
    this._trigger = null;
    if (trigger) trigger.focus({ preventScroll: true });
  }

  _paintDialog(card, error) {
    const body = this._dialog.querySelector("[data-dlg-body]");
    const variant = this._variant(card);
    const price = body.querySelector("[data-dlg-price]");
    if (price) price.textContent = variant.price;
    const radio = body.querySelector(`[data-dlg-variant-input][value="${card.variantId}"]`);
    if (radio && !radio.checked) radio.checked = true;
    const name = body.querySelector("[data-dlg-variant-name]");
    if (name) name.textContent = variant.title;
    // Keep the shared pl-variant-dd (PUMPLAB_MOBILE_VARIANT_DD_V1) in step when the variant changes from our side
    const dd = body.querySelector(".pl-variant-dd");
    if (dd) {
      const opt = dd.querySelector(`.pl-variant-dd__option[data-value="${card.variantId}"]`);
      if (opt && !opt.classList.contains("is-selected")) {
        dd.querySelectorAll(".pl-variant-dd__option").forEach((o) => o.classList.toggle("is-selected", o === opt));
        const current = dd.querySelector(".pl-variant-dd__current");
        current.replaceChildren();
        const nameEl = document.createElement("span");
        nameEl.className = "pl-variant-dd__name";
        nameEl.textContent = variant.title;
        current.append(nameEl);
        const stock = opt.querySelector(".pl-stock");
        if (stock) current.append(stock.cloneNode(true));
      }
    }
    const cta = body.querySelector("[data-dlg-cta]");
    if (!cta) return;
    // custom-button wraps its children on upgrade, so write into the label span, not the button
    const label = cta.querySelector("[data-dlg-cta-label]") || cta;
    const soldOut = !variant.available;
    label.textContent = error || (soldOut ? card.data.soldOut : variant.cta);
    cta.disabled = soldOut;
  }

  // Show the shared images plus the selected flavour's set (alt "#Smak_<flavour>", see the Liquid comment)
  _renderGallery(card) {
    const track = this._dialog.querySelector("[data-dlg-track]");
    if (!track) return;
    const key = (this._variant(card).title || "").toLowerCase();
    const slides = Array.from(track.children);
    const own = slides.filter((s) => s.dataset.group === key);
    slides.forEach((s) => { s.hidden = !(s.dataset.group === "" || s.dataset.group === key); });
    if (!own.length && !slides.some((s) => !s.hidden)) slides.forEach((s) => { s.hidden = false; });
    // flavour images first, then the shared ones (the product page leads with the selected flavour)
    own.slice().reverse().forEach((s) => track.prepend(s));
    const visible = slides.filter((s) => !s.hidden).length;
    const dots = this._dialog.querySelector("[data-dlg-dots]");
    if (dots) dots.replaceChildren(...Array.from({ length: visible > 1 ? visible : 0 }, () => document.createElement("span")));
    this._dialog.querySelectorAll("[data-dlg-prev], [data-dlg-next]").forEach((b) => { b.hidden = visible < 2; });
    track.scrollLeft = 0;
    this._syncDots();
  }

  _slideCount() { return this._dialog.querySelectorAll(".pl-addons-dlg__slide:not([hidden])").length; }

  _go(dir) {
    const track = this._dialog && this._dialog.querySelector("[data-dlg-track]");
    if (!track) return;
    const i = clampIndex(Math.round(track.scrollLeft / Math.max(1, track.clientWidth)) + dir, this._slideCount());
    track.scrollTo({ left: i * track.clientWidth, behavior: "smooth" });
  }

  // "Läs mer": the description is clamped to 3 lines; the toggle only shows when text is actually cut off
  _bindDesc(body) {
    const desc = body.querySelector("[data-dlg-desc]");
    const more = body.querySelector("[data-dlg-more]");
    if (!desc || !more) return;
    const label = more.querySelector("[data-dlg-more-label]");
    // Skip the packaging intro the copy opens with ("Kosttillskott i pulverform." + "Vad du kan förvänta dig:" list);
    // the modal starts at the first selling paragraph. A lone intro sentence inside a real paragraph is trimmed.
    const prose = desc.querySelector(".prose");
    const isIntro = (t) => /^(kosttillskott|dietary supplement)\b/i.test(t);
    while (prose && prose.firstElementChild) {
      const el = prose.firstElementChild;
      const text = el.textContent.replace(/\s+/g, " ").trim();
      if (el.tagName === "P" && (!text || text.endsWith(":") || (isIntro(text) && text.length < 80))) {
        const next = el.nextElementSibling;
        el.remove();
        if (text.endsWith(":") && next && /^(UL|OL)$/.test(next.tagName)) next.remove();
        continue;
      }
      if (/^(UL|OL)$/.test(el.tagName) && !prose.querySelector("p")) break;
      if (el.tagName === "P" && isIntro(text)) {
        const first = el.firstChild;
        if (first && first.nodeType === Node.TEXT_NODE) first.data = first.data.replace(/^[^.]*\.\s*/, "");
      }
      break;
    }
    // ...and stop before the facts block ("Innehåll per portion (1 skopa):" heading + list): body text only
    if (prose) {
      const bodyEnd = [...prose.children].find((el) => {
        const t = el.textContent.replace(/\s+/g, " ").trim();
        return el.tagName !== "P" || t.endsWith(":") || (el.querySelector("strong, b") && el.querySelector("strong, b").textContent.trim() === t);
      });
      if (bodyEnd) while (bodyEnd.nextSibling) bodyEnd.nextSibling.remove();
      if (bodyEnd) bodyEnd.remove();
    }
    // the product page tab bolds the product name in the first and the last body paragraph; do the same
    const title = (desc.dataset.title || "").trim();
    if (title) {
      const re = new RegExp(`\\b${title.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`);
      const paras = [...desc.querySelectorAll(".prose p")];
      const boldMention = (p, last) => {
        if (!p || p.querySelector("strong, b")) return;
        const walker = document.createTreeWalker(p, NodeFilter.SHOW_TEXT);
        const hits = [];
        for (let node = walker.nextNode(); node; node = walker.nextNode()) {
          const m = re.exec(node.data);
          if (m) hits.push([node, m]);
        }
        const pick = last ? hits[hits.length - 1] : hits[0];
        if (!pick) return;
        const [node, m] = pick;
        const hit = node.splitText(m.index);
        hit.splitText(m[0].length);
        const strong = document.createElement("strong");
        hit.replaceWith(strong);
        strong.append(hit);
      };
      boldMention(paras[0], false);
      if (paras.length > 1) boldMention(paras[paras.length - 1], true);
    }
    requestAnimationFrame(() => {
      more.hidden = desc.scrollHeight <= desc.clientHeight + 2;
    });
    more.addEventListener("click", () => {
      const open = more.getAttribute("aria-expanded") !== "true";
      const from = desc.getBoundingClientRect().height;
      desc.classList.toggle("is-open", open);
      const to = desc.getBoundingClientRect().height;
      more.setAttribute("aria-expanded", String(open));
      label.textContent = open ? more.dataset.less : more.dataset.more;
      if (matchMedia("(prefers-reduced-motion: reduce)").matches) return;
      desc.animate([{ height: `${from}px` }, { height: `${to}px` }], { duration: 320, easing: "cubic-bezier(.22, 1, .36, 1)" });
    });
  }

  _syncDots() {
    const track = this._dialog.querySelector("[data-dlg-track]");
    const dots = this._dialog.querySelectorAll("[data-dlg-dots] span");
    if (!track || !dots.length) return;
    const i = clampIndex(Math.round(track.scrollLeft / Math.max(1, track.clientWidth)), dots.length);
    dots.forEach((d, j) => d.classList.toggle("is-on", j === i));
  }
}

if (!customElements.get("pl-addons")) customElements.define("pl-addons", PlAddons);
